import {
  renderThirdLogin,
  toBackendLang,
  useLogin,
  useTelegramLogin,
  type ThirdSource,
} from "@kupikupi/api";
import { API_BASE_URL, FEATURE_VK, MOCK_TG } from "@kupikupi/brand";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { useAuth, setAuthToken } from "../lib/auth";
import { getInviteCode, getRawInitData } from "../lib/telegram-auth";

const IS_MOCK = MOCK_TG;

/** ─── Third-party login button (inline — no shared component needed) ─────── */
interface ThirdBtnProps {
  source: ThirdSource;
  label: string;
  icon: string;
  iconStyle?: React.CSSProperties;
  fullWidth?: boolean;
  onError: (source: string) => void;
}

function ThirdBtn({ source, label, icon, iconStyle, fullWidth, onError }: ThirdBtnProps) {
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Callback must match provider-registered URI; mini app uses the web origin.
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const inviteCode = getInviteCode();
      const renderInput = {
        source,
        lang: toBackendLang("ru"),
        callback: `${origin}/oauth/callback/${source}`,
        ...(inviteCode ? { inviteCode } : {}),
      };
      const oauthUrl = await renderThirdLogin({ baseUrl: API_BASE_URL }, renderInput);
      if (!oauthUrl) throw new Error("empty oauth url");
      window.location.href = oauthUrl;
    } catch {
      setLoading(false);
      onError(source);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-busy={loading}
      style={{
        gridColumn: fullWidth ? "1 / -1" : undefined,
        padding: "11px 8px",
        background: "#fff",
        border: "1.5px solid var(--color-border)",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        opacity: loading ? 0.6 : 1,
        cursor: loading ? "not-allowed" : "pointer",
        color: "var(--color-text)",
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          fontWeight: 900,
          flexShrink: 0,
          ...iconStyle,
        }}
      >
        {icon}
      </span>
      {label}
    </button>
  );
}

/** ─── Login page ──────────────────────────────────────────────────────────── */
export function LoginPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { token: existingToken } = useAuth();

  const tgLogin = useTelegramLogin();
  const emailLogin = useLogin();

  const [devToken, setDevToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Reverse-guard: already signed in → go home
  if (existingToken) {
    navigate({ to: "/" as never });
    return null;
  }

  // ─── Telegram one-tap ─────────────────────────────────────────────────────
  const onTelegramLogin = async () => {
    const initData = getRawInitData();
    if (!initData) return;
    try {
      setLoginError(null);
      const inviteCode = getInviteCode();
      const result = await tgLogin.mutateAsync(
        inviteCode ? { initData, inviteCode } : { initData },
      );
      if (result.xaccessToken) {
        setAuthToken(result.xaccessToken);
        navigate({ to: "/" as never });
      }
    } catch {
      setLoginError(t("login.login_failed"));
    }
  };

  // ─── Dev token bypass (MOCK_TG only) ──────────────────────────────────────
  const onDevTokenSubmit = () => {
    const token = devToken.trim();
    if (!token) return;
    setAuthToken(token);
    navigate({ to: "/" as never });
  };

  // ─── Email / password form ────────────────────────────────────────────────
  const onEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoginError(null);
    try {
      const resp = await emailLogin.mutateAsync({ username: email, password });
      if (resp?.xaccessToken) {
        setAuthToken(resp.xaccessToken);
        navigate({ to: "/" as never });
      }
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : t("login.login_failed"));
    }
  };

  const canSubmit = !!email && !!password && !emailLogin.isPending && !tgLogin.isPending;
  const anyPending = emailLogin.isPending || tgLogin.isPending;

  const onThirdError = (source: string) =>
    setLoginError(t("third_login.error", { provider: t(`third_login.${source}`) }));

  return (
    <TgPage tabbar={false}>
      {/* ─── Gradient background ─────────────────────────────────────────── */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "linear-gradient(180deg,#E0F7F1,#B8E8DD)",
          zIndex: -1,
        }}
      />

      {/* ─── Scrollable body ─────────────────────────────────────────────── */}
      <div
        className="tg-body"
        style={{
          flex: 1,
          padding: "24px 20px",
          gap: 0,
          overflowY: "auto",
        }}
      >
        {/* Brand logo row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background:
                "linear-gradient(135deg,var(--color-primary),var(--color-primary-fill))",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: 18,
              fontWeight: 900,
              fontStyle: "italic",
              fontFamily: "Times, serif",
              flexShrink: 0,
            }}
          >
            k
          </span>
          <strong
            style={{
              fontFamily: "-apple-system, system-ui, sans-serif",
              fontSize: 18,
              fontWeight: 800,
            }}
          >
            kupikupi
          </strong>
        </div>

        {/* Hero title */}
        <h1
          style={{
            fontFamily: "-apple-system, system-ui, sans-serif",
            fontSize: 24,
            fontWeight: 900,
            margin: "0 0 6px",
            letterSpacing: "-0.015em",
            lineHeight: 1.1,
          }}
        >
          {t("pitch.welcome_back_1")}
          <br />
          {t("pitch.welcome_back_2")}
          <br />
          {t("pitch.welcome_back_3")}
        </h1>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "0 0 24px" }}>
          {t("pitch.welcome_back_sub")}
        </p>

        {/* Telegram one-tap */}
        <button
          type="button"
          onClick={onTelegramLogin}
          disabled={anyPending}
          style={{
            width: "100%",
            padding: 13,
            background: "#229ED9",
            color: "#fff",
            border: 0,
            borderRadius: 14,
            fontWeight: 800,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 8,
            opacity: tgLogin.isPending ? 0.6 : 1,
            cursor: anyPending ? "not-allowed" : "pointer",
          }}
        >
          {tgLogin.isPending ? (
            <span
              aria-hidden="true"
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.4)",
                borderTopColor: "#fff",
                animation: "ml-spin 0.8s linear infinite",
                display: "inline-block",
              }}
            />
          ) : (
            <span>✈</span>
          )}
          {t("login.telegram_btn")}
        </button>

        {/* Third-party login grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            marginBottom: 0,
          }}
        >
          <ThirdBtn
            source="google"
            label="Google"
            icon="G"
            iconStyle={{ background: "#f1f1f1", color: "#4285F4" }}
            onError={onThirdError}
          />
          <ThirdBtn
            source="yandex"
            label="Yandex"
            icon="Я"
            iconStyle={{ background: "#FF0000", color: "#fff" }}
            onError={onThirdError}
          />
          {FEATURE_VK && (
            <ThirdBtn
              source="vk"
              label="ВКонтакте"
              icon="VK"
              iconStyle={{ background: "#0077FF", color: "#fff", borderRadius: 6 }}
              fullWidth
              onError={onThirdError}
            />
          )}
        </div>

        {/* Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "14px 0",
          }}
        >
          <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 9,
              color: "var(--color-text-muted)",
            }}
          >
            {t("login.or_email")}
          </span>
          <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
        </div>

        {/* Email / password form */}
        <form id="login-form" onSubmit={onEmailSubmit}>
          {/* Email */}
          <div style={{ marginBottom: 10 }}>
            <label
              htmlFor="login-email"
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 4,
                color: "var(--color-text)",
              }}
            >
              {t("login.email")}
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.email_placeholder")}
              autoComplete="email"
              disabled={anyPending}
              style={{
                width: "100%",
                height: 40,
                padding: "0 12px",
                border: "1.5px solid var(--color-border)",
                borderRadius: 10,
                fontSize: 13,
                background: "#fff",
                color: "var(--color-text)",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="login-password"
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 4,
                color: "var(--color-text)",
              }}
            >
              <span>{t("login.password")}</span>
              <a
                href="#"
                style={{
                  color: "var(--color-primary-fill)",
                  fontSize: 10,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
                onClick={(e) => e.preventDefault()}
              >
                {t("login.forgot")}
              </a>
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="login-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("login.password_placeholder")}
                autoComplete="current-password"
                disabled={anyPending}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 40px 0 12px",
                  border: "1.5px solid var(--color-border)",
                  borderRadius: 10,
                  fontSize: 13,
                  background: "#fff",
                  color: "var(--color-text)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  color: "var(--color-text-muted)",
                  fontSize: 14,
                  padding: 4,
                  lineHeight: 1,
                }}
                aria-label="Toggle password visibility"
              >
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {/* Remember me */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 12,
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                border: "1.5px solid var(--color-primary-fill)",
                borderRadius: 4,
                background: "var(--color-primary-fill)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ color: "#fff", fontSize: 9, lineHeight: 1 }}>✓</span>
            </span>
            {t("login.remember")}
          </label>
        </form>

        {/* Error */}
        {loginError && (
          <p
            style={{
              fontSize: 12,
              color: "var(--color-danger)",
              margin: "10px 0 0",
              textAlign: "center",
            }}
          >
            {IS_MOCK && loginError === t("login.login_failed")
              ? "Mock initData 无法通过后端验证。请在下方粘贴真实 JWT token。"
              : loginError}
          </p>
        )}

        {/* Dev token bypass */}
        {IS_MOCK && (
          <div
            style={{
              marginTop: 20,
              borderTop: "1px solid var(--color-border)",
              paddingTop: 16,
            }}
          >
            <p
              style={{
                fontSize: 11,
                color: "var(--color-text-muted)",
                margin: "0 0 8px",
                textAlign: "center",
              }}
            >
              Dev: 粘贴 JWT token 跳过验证
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={devToken}
                onChange={(e) => setDevToken(e.target.value)}
                placeholder="xaccessToken..."
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  fontSize: 13,
                  fontFamily: "monospace",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                onClick={onDevTokenSubmit}
                style={{
                  background: "var(--color-primary-fill)",
                  color: "#fff",
                  border: 0,
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                登录
              </button>
            </div>
          </div>
        )}

        {/* Footer: ToS + register link */}
        <p
          style={{
            fontSize: 10,
            color: "var(--color-text-muted)",
            marginTop: 18,
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          {/*
            login.foot uses next-intl rich-text tags (<terms>/<privacy>).
            react-i18next's <Trans> renders them as React nodes.
            The key maps to e.g. "Войдя, вы соглашаетесь с <terms>офертой</terms> и…"
          */}
          <Trans
            i18nKey="login.foot"
            ns="auth"
            components={{
              terms: (
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  style={{ color: "var(--color-primary-fill)", fontWeight: 700 }}
                />
              ),
              privacy: (
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  style={{ color: "var(--color-primary-fill)", fontWeight: 700 }}
                />
              ),
            }}
          />
          <br />
          <a
            href="#"
            style={{
              color: "var(--color-primary-fill)",
              fontWeight: 700,
              textDecoration: "none",
            }}
            onClick={(e) => {
              e.preventDefault();
              navigate({ to: "/register" as never });
            }}
          >
            {t("login.no_account")} {t("login.register_link")}
          </a>
        </p>
      </div>

      {/* ─── Bottom CTA: 登录 ─────────────────────────────────────────────── */}
      <TgMain
        type="submit"
        disabled={!canSubmit}
        onClick={() => {
          const form = document.getElementById("login-form") as HTMLFormElement | null;
          form?.requestSubmit();
        }}
      >
        {emailLogin.isPending ? t("login.submitting") : t("login.submit")}
      </TgMain>
    </TgPage>
  );
}
